import {
    excludeSymbolsRegExp,
    bloggersRegisterProfileRegExp,
    instagramProfileRegExp,
    telegramProfileRegExp,
    vkProfileRegExp,
    youtubeProfileRegExp,
} from '../../../utils/regexps';
import { composeValidators } from '../../../utils/validators/compose';
import { makeClickOutLinkValidator } from '../../../utils/validators/makeClickOutLinkValidator';
import { makeMaxLengthValidator } from '../../../utils/validators/makeMaxLengthValidator';
import { makeRegExpValidator } from '../../../utils/validators/makeRegExpValidator';
import { makeRequiredValidator } from '../../../utils/validators/makeRequiredValidator';
import { makeUrlValidator } from '../../../utils/validators/makeUrlValidator';
import { BusinessType } from '../types';

export const validateDisplayName = composeValidators(
    makeRequiredValidator<string>(),
    makeMaxLengthValidator(25, 'Название профиля не должно превышать 25 символов'),
    makeRegExpValidator(excludeSymbolsRegExp, 'Не используйте запрещенные символы  ; > < /'),
);

export const validateDomain = composeValidators(
    makeRequiredValidator<string>(),
    makeUrlValidator(),
    makeClickOutLinkValidator('В качестве сайта вы можете использовать ваш сайт или маркетплейс.'),
);

export const validateUrl = makeUrlValidator();

export const validateInstagram = composeValidators(
    makeRegExpValidator(
        instagramProfileRegExp,
        'Укажите ссылку в формате https://www.instagram.com/username',
    ),
);

export const validateTelegram = composeValidators(
    makeRegExpValidator(telegramProfileRegExp, 'Укажите ссылку в формате https://t.me/username'),
);

export const validateVk = composeValidators(
    makeRegExpValidator(vkProfileRegExp, 'Укажите ссылку в формате https://vk.com/username'),
);

export const validateYoutube = composeValidators(
    makeRegExpValidator(
        youtubeProfileRegExp,
        'Укажите ссылку в формате https://youtube.com/@username',
    ),
);

export const validateBloggersRegister = composeValidators(
    makeRegExpValidator(
        bloggersRegisterProfileRegExp,
        'Укажите ссылку в формате https://www.gosuslugi.ru/...',
    ),
);

export const validateOfferAgreement = composeValidators(makeRequiredValidator<string>());

export const textDictionary = {
    [BusinessType.BUSINESS]: {
        socialLinks: 'Социальные сети вашего бизнеса',
        socialLinksHint:
            'Вводите социальные сети, которые принадлежат вашему бизнесу. Принадлежность аккаунта вам будет проверяться.',
        instagramLink: 'Instagram бизнеса¹',
        telegramLink: 'Телеграм-канал бизнеса',
        registerDisclaimer:
            'Данные о бизнесе заполняются один раз, изменить их можно будет только через поддержку. Одному логину может принадлежать один бизнес.',
        socialAccountInstagramConfirm:
            'Если вы указали Instagram*, необходимо подтвердить ваш доступ к социальной сети бизнеса.',
        socialAccountTgConfirm: 'Необходимо подтвердить доступ к телеграм-каналу.',
        verificationDisclaimer: 'После мы получим ваше сообщение и отправим на проверку.',
        accountAccessVerification: 'подтверждает доступ к аккаунту бизнеса.',
        declinedAccountStatus:
            'К сожалению, не сможем принять ваш бизнес в Яндекс Ритм. Это связано с категорией товаров и услуг вашего бизнеса — подробнее рассказали в ',
        declinedForBetaStatus:
            'К сожалению, мы не можем пригласить ваш бизнес в бету. Мы свяжемся с вами, когда этап бета-тестирования завершится, и появится возможность попасть в Ритм.',
        accountRegistered: 'Ваш бизнес-аккаунт успешно зарегистрирован',
        logPage: 'Регистрации бизнеса',
        submitButtonText: 'Подать заявку',
        submitSuccessText: 'Заявка на регистрацию подана',
        submitFailedText: 'Не удалось подать заявку на регистрацию',
    },
    [BusinessType.BLOGGER]: {
        socialLinks: 'Ваши публичные социальные сети',
        socialLinksHint:
            'Вводите социальные сети, которые принадлежат вам. Принадлежность аккаунта будет проверяться.\n\nПожалуйста, заполните все поля, чтобы увеличить шанс попасть в бету',
        instagramLink: 'Instagram¹',
        telegramLink: 'Телеграм-канал',
        registerDisclaimer:
            'Данные об аккаунте заполняются один раз, изменить их можно будет только через поддержку. Одному логину может принадлежать один бизнес.',
        socialAccountInstagramConfirm:
            'Если вы указали Instagram*, необходимо подтвердить ваш доступ к аккаунту.',
        socialAccountTgConfirm: 'Необходимо подтвердить доступ к телеграм-каналу.',
        verificationDisclaimer:
            'После мы получим ваше сообщение и добавим вас в список желающих попасть в бету.',
        accountAccessVerification: 'подтверждает доступ к аккаунту.',
        declinedAccountStatus:
            'К сожалению, не можем принять вас в Яндекс Ритм. Это связано с тем, что тематика вашего блога не соответствует правилам сервиса - подробнее рассказали в ',
        declinedForBetaStatus:
            'К сожалению, мы не можем пригласить ваш аккаунт в бету. Мы свяжемся с вами, когда этап бета-тестирования завершится, и появится возможность попасть в Ритм.',
        accountRegistered: 'Ваш аккаунт успешно зарегистрирован',
        logPage: 'Регистрации блогера',
        submitButtonText: 'Хочу участвовать в бете',
        submitSuccessText: 'Заявка на участие в бете подана',
        submitFailedText: 'Не удалось подать заявку на участие в бете',
    },
    [BusinessType.SERVICE]: {
        socialLinks: 'Социальные сети вашего бизнеса',
        socialLinksHint:
            'Вводите социальные сети, которые принадлежат вашему бизнесу. Принадлежность аккаунта вам будет проверяться.',
        instagramLink: 'Instagram бизнеса¹',
        telegramLink: 'Телеграм-канал бизнеса',
        registerDisclaimer:
            'Данные о бизнесе заполняются один раз, изменить их можно будет только через поддержку. Одному логину может принадлежать один бизнес.',
        socialAccountInstagramConfirm:
            'Если вы указали Instagram*, необходимо подтвердить ваш доступ к социальной сети бизнеса.',
        socialAccountTgConfirm: 'Необходимо подтвердить доступ к телеграм-каналу.',
        verificationDisclaimer: 'После мы получим ваше сообщение и отправим на проверку.',
        accountAccessVerification: 'подтверждает доступ к аккаунту бизнеса.',
        declinedAccountStatus:
            'К сожалению, не сможем принять ваш бизнес в Яндекс Ритм. Это связано с категорией товаров и услуг вашего бизнеса — подробнее рассказали в ',
        declinedForBetaStatus:
            'К сожалению, мы не можем пригласить ваш бизнес в бету. Мы свяжемся с вами, когда этап бета-тестирования завершится, и появится возможность попасть в Ритм.',
        accountRegistered: 'Ваш бизнес-аккаунт успешно зарегистрирован',
        logPage: 'Регистрации бизнеса',
        submitButtonText: 'Отправить заявку',
        submitSuccessText: 'Заявка на регистрацию подана',
        submitFailedText: 'Не удалось подать заявку на регистрацию',
    },
};
